import { CompanyGuid, OnlineBookingType } from '@breezy/shared'
import { faScrewdriverWrench, faTruckFast } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Skeleton } from 'antd'
import classNames from 'classnames'
import React, { useCallback, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { SmartAccountExperienceCompanyInfoBox } from '../../components/AccountExperienceCompanyInfoBox/SmartAccountExperienceCompanyInfoBox'
import { SmartAccountExperienceCompanyInfoSection } from '../../components/AccountExperienceCompanyInfoBox/SmartAccountExperienceCompanyInfoSection'
import { Banner } from '../../elements/Banner/Banner'
import { HtmlRenderer } from '../../elements/HtmlRenderer/HtmlRenderer'
import { useIsLargeScreen, useIsMobile, useIsSmallScreen } from '../../hooks/useIsMobile'
import { UnauthPricebookSettingsProvider } from '../../hooks/usePricebookSettings'
import { GooglePlacesWrapper } from '../../providers/GoogleMapsPlaces'
import { HeroIconWithTitleAndMessage } from '../../ui/HeroIconWithTitleAndMessage/HeroIconWithTitleAndMessage'
import { OnsitePageSection } from '../../ui/OnsitePage/OnsitePageSection'
import { useOnsitePageDimensions } from '../../ui/OnsitePage/onsitePageUtils'
import { useStrictContext } from '../../utils/react-utils'
import { BookMaintenancePage } from './BookMaintenancePage'
import { OnlineBookingForm } from './OnlineBookingForm'
import {
  OnlineBookingCompanyConfigContext,
  WithOnlineBookingCompanyConfigContext,
  WithOnlineBookingContexts,
} from './onlineBookingUtils'

export const AccountExperienceBookNowPage = React.memo(() => {
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const companyGuid = queryParams.get('companyGuid')
  const visitGuid = queryParams.get('visitGuid')
  if (!companyGuid && !visitGuid) {
    return <div>No companyGuid or visitGuid provided</div>
  }

  if (visitGuid) {
    return <BookMaintenancePage maintenancePlanVisitGuid={visitGuid} />
  }

  if (companyGuid) {
    return <AccountExperienceJobRequestPageInner companyGuid={companyGuid} />
  }

  return <div>No companyGuid or visitGuid provided</div>
})

AccountExperienceBookNowPage.displayName = 'AccountExperienceBookNowPage'

const AccountExperienceJobRequestPageInner = React.memo(({ companyGuid }: { companyGuid: string }) => {
  const isMobile = useIsMobile()
  const isSmallScreen = useIsSmallScreen()
  const isLargeScreen = useIsLargeScreen()
  const pageDimensions = useOnsitePageDimensions()

  const [step, setStep] = useState<'pre-submit' | 'submitted-request' | 'submitted-instant-booking'>('pre-submit')

  const paddingClassName = useMemo(() => {
    if (isMobile) {
      return 'py-10'
    }
    if (isSmallScreen) {
      return 'py-16'
    }
    return 'p-16'
  }, [isMobile, isSmallScreen])

  // biome-ignore lint/correctness/useExhaustiveDependencies: setStep is a state setter and is stable
  const onSubmitted = useCallback(
    (bookingType: OnlineBookingType) => {
      setStep(bookingType === 'REQUEST' ? 'submitted-request' : 'submitted-instant-booking')
    },
    [setStep],
  )
  return (
    <UnauthPricebookSettingsProvider companyGuid={companyGuid}>
      <GooglePlacesWrapper>
        <div className={classNames('flex flex-row items-start justify-center', paddingClassName)}>
          {isLargeScreen && <SmartAccountExperienceCompanyInfoBox companyGuid={companyGuid} />}
          <div
            style={pageDimensions.style}
            // The estimates always have this padding on the top and bottom because the estimate sections do it that
            // way. It's accounted for everywhere, but here it makes the top of the first section not align with the
            // company info box.
            className={isMobile ? 'my-[-16px]' : 'my-[-24px]'}
          >
            <OnsitePageSection padding="large-mobile">
              {step === 'pre-submit' && (
                <HeroIconWithTitleAndMessage
                  title="Online booking"
                  message="Please fill out the form below. Providing detailed information will help us deliver the best possible service."
                  faIcon={faScrewdriverWrench}
                  color="gray"
                />
              )}

              {step === 'submitted-request' || step === 'submitted-instant-booking' ? (
                <OnlineBookingSubmittedPage
                  companyGuid={companyGuid}
                  bookingType={step === 'submitted-request' ? 'REQUEST' : 'INSTANT'}
                />
              ) : null}
            </OnsitePageSection>
            {step === 'pre-submit' && (
              <WithOnlineBookingContexts companyGuid={companyGuid} loadingComponent={<OnlineBookingFormSkeleton />}>
                <OnlineBookingForm companyGuid={companyGuid} onSubmit={onSubmitted} />
              </WithOnlineBookingContexts>
            )}
            {!isLargeScreen && <SmartAccountExperienceCompanyInfoSection companyGuid={companyGuid} />}
          </div>
        </div>
      </GooglePlacesWrapper>
    </UnauthPricebookSettingsProvider>
  )
})

type OnlineBookingSubmittedPageProps = {
  companyGuid: CompanyGuid
  bookingType: OnlineBookingType
}

export const OnlineBookingSubmittedPage = React.memo(
  ({ companyGuid, bookingType }: OnlineBookingSubmittedPageProps) => {
    return (
      <>
        <HeroIconWithTitleAndMessage
          title={bookingType === 'REQUEST' ? 'Request submitted' : 'Booking confirmed'}
          message={
            bookingType === 'REQUEST'
              ? 'Thank you for your request! We’ve received your details and will contact you shortly to confirm your appointment and provide any additional information.'
              : 'Thank you for booking your appointment! Your selected time has been confirmed, and our technician will arrive as scheduled. We’ll contact you shortly if we need any additional information.'
          }
        />
        <WithOnlineBookingCompanyConfigContext
          companyGuid={companyGuid}
          loadingComponent={<OnlineBookingFormSkeleton rows={2} />}
        >
          <AfterHoursBanner />
        </WithOnlineBookingCompanyConfigContext>
      </>
    )
  },
)

OnlineBookingSubmittedPage.displayName = 'OnlineBookingSubmittedPage'

export const AfterHoursBanner = React.memo(() => {
  const { afterHoursBannerHtml } = useStrictContext(OnlineBookingCompanyConfigContext)

  if (!afterHoursBannerHtml) {
    return null
  }

  return (
    <div className="pt-2">
      <Banner>
        <div className="flex items-center gap-3">
          <div>
            <div
              className={classNames(
                'mx-auto flex h-[32px] w-[32px] items-center justify-center rounded-full bg-bz-orange-200',
              )}
            >
              <FontAwesomeIcon className="text-bz-orange-800" icon={faTruckFast} />
            </div>
          </div>
          <HtmlRenderer
            // This will remove the bottom margin from the paragraph element in the HTML
            className="*:last:mb-0"
            htmlContent={afterHoursBannerHtml}
          />
        </div>
      </Banner>
    </div>
  )
})

AfterHoursBanner.displayName = 'AfterHoursBanner'

type OnlineBookingFormSkeletonProps = {
  rows?: number
}

export const OnlineBookingFormSkeleton = React.memo(({ rows = 6 }: OnlineBookingFormSkeletonProps) => {
  return (
    <div className="p-4">
      <Skeleton paragraph={{ rows }} />
    </div>
  )
})

OnlineBookingFormSkeleton.displayName = 'OnlineBookingFormSkeleton'
